import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, alpha, Box, Button, Grid, IconButton, Link, Typography, useTheme } from '@mui/material';
import { useFieldArray, useWatch } from 'react-hook-form';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { FamilyPersonCard, EMPTY_PERSON, getFamilyRelationOptions, getGuestRelationOptions } from './FamilyPersonCard';
import ETextField from '@ui/pages/EventRegistration/RegistrationForm/components/ETextField';
import ECheckboxGroup from '@ui/pages/EventRegistration/RegistrationForm/components/ECheckboxGroup';
import HTimeline from '@ui/components/Timeline';
import { useAppDispatch } from '@application/store/useStore';
import { getFamilyFestCompaniesAsync, getFamilyFestPickupLocationsAsync } from '@application/features/familyFest/familyFestAttendeesThunk';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import FamilyLegalTermsDialog from './legal/FamilyLegalTermsDialog';
import FamilyLegalPrivacyDialog from './legal/FamilyLegalPrivacyDialog';
import { mapPickupLocationToOption, getPickupLocationInputLabel, buildSavedPickupLocationOption } from '@application/utilities/pickup-location-utils';
const normalized = (value) => String(value ?? '')
    .toLowerCase()
    .trim();
/** Guest/member rows have no `eventChoice`; treat them as attending when primary is family or guest flow. */
const isPersonAttendingForTransport = (p, index) => {
    if (index > 0)
        return true;
    return normalized(p?.eventChoice) !== 'false';
};
const termsAcceptedRules = (t) => ({
    validate: (v) => Array.isArray(v) && v.includes('true')
        ? true
        : t('familyForm.fieldRequired') || 'This field is required'
});
const FamilyFormContent = ({ control, errors, setValue, isSaved, activeStep: _activeStep, onStepChange: _onStepChange, showTerms: _showTerms = false, isEditMode = false, maxPersons = null, savedPersonCount = 0, savedComingWith = null }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const [termsDialogOpen, setTermsDialogOpen] = useState(false);
    const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
    const termsAndPrivacyLabel = (_jsxs(_Fragment, { children: [t('familyForm.iAgreePrefix') || 'I agree to the ', _jsx(Link, { component: 'button', type: 'button', underline: 'always', onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTermsDialogOpen(true);
                }, sx: { fontSize: 'inherit', fontWeight: 500, verticalAlign: 'baseline', ml: '0 !important' }, children: t('familyForm.termsAndConditions') || 'terms and conditions' }), ' ', t('common.and') || 'and', ' ', _jsx(Link, { component: 'button', type: 'button', underline: 'always', onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPrivacyDialogOpen(true);
                }, sx: { fontSize: 'inherit', fontWeight: 500, verticalAlign: 'baseline', ml: '0 !important' }, children: t('familyForm.privacyStatement') || 'privacy statement' }), "."] }));
    const [companyOptions, setCompanyOptions] = useState([]);
    const [pickupLocationsByCountry, setPickupLocationsByCountry] = useState({});
    const [removeMemberConfirmOpen, setRemoveMemberConfirmOpen] = useState(false);
    const [pendingRemoveMemberIndex, setPendingRemoveMemberIndex] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const data = await dispatch(getFamilyFestCompaniesAsync()).unwrap();
                if (!cancelled && Array.isArray(data)) {
                    const opts = data
                        .map((row) => ({
                        label: String(row.name ?? row.Name ?? ''),
                        value: String(row.id ?? row.Id ?? '')
                    }))
                        .filter((o) => o.value.length > 0 && o.label.length > 0);
                    setCompanyOptions(opts);
                }
            }
            catch {
                if (!cancelled)
                    setCompanyOptions([]);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch]);
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'persons'
    });
    const persons = useWatch({ control, name: 'persons' }) ?? [];
    const watchedCompanyId = useWatch({ control, name: 'persons.0.company' });
    const prevCompanyIdRef = useRef(undefined);
    const allCountriesKey = persons
        .map((p) => String(p?.addressCountry || 'nl').toLowerCase().trim())
        .join(',');
    const toPickupOpts = (data) => data.map(mapPickupLocationToOption);
    useEffect(() => {
        let cancelled = false;
        const uniqueCountries = Array.from(new Set(persons.map((p) => String(p?.addressCountry || 'nl').toLowerCase().trim())));
        const load = async () => {
            const companyId = watchedCompanyId || undefined;
            const results = {};
            await Promise.all(uniqueCountries.map(async (country) => {
                try {
                    const data = await dispatch(getFamilyFestPickupLocationsAsync({ addressCountry: country, companyId })).unwrap();
                    if (!cancelled && Array.isArray(data)) {
                        results[country] = toPickupOpts(data);
                    }
                }
                catch {
                    if (!cancelled)
                        results[country] = [];
                }
            }));
            if (!cancelled)
                setPickupLocationsByCountry(results);
        };
        // Clear pickup selections only when the user changes from one company to another.
        if (prevCompanyIdRef.current &&
            watchedCompanyId &&
            prevCompanyIdRef.current !== watchedCompanyId) {
            const fieldCount = persons?.length ?? 0;
            for (let i = 0; i < fieldCount; i++) {
                setValue?.(`persons.${i}.transportLocation`, '');
            }
        }
        prevCompanyIdRef.current = watchedCompanyId;
        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, watchedCompanyId, allCountriesKey]);
    const allPickupOptionsById = useMemo(() => {
        const map = new Map();
        Object.values(pickupLocationsByCountry).forEach((countryOptions) => {
            countryOptions.forEach((option) => map.set(option.value, option));
        });
        return map;
    }, [pickupLocationsByCountry]);
    const getPickupOptionsForPerson = (person) => {
        const country = String(person?.addressCountry || 'nl').toLowerCase().trim();
        const countryOptions = pickupLocationsByCountry[country] ?? [];
        const selectedId = String(person?.transportLocation ?? '').trim();
        if (!selectedId)
            return countryOptions;
        if (countryOptions.some((option) => option.value === selectedId))
            return countryOptions;
        const savedOption = buildSavedPickupLocationOption(person);
        const selectedOption = allPickupOptionsById.get(selectedId) ?? savedOption;
        if (selectedOption) {
            return [selectedOption, ...countryOptions.filter((option) => option.value !== selectedId)];
        }
        return countryOptions;
    };
    const pickupLocationOptions = getPickupOptionsForPerson(persons?.[0]);
    const anyAlone = persons.some((p) => normalized(p?.ComingWith) === 'alone');
    const anyEventNo = persons.some((p) => String(p?.eventChoice ?? '')
        .toLowerCase()
        .trim() === 'false');
    const primaryComingWith = normalized(persons?.[0]?.ComingWith);
    const primaryFamilyOrGuest = primaryComingWith === 'family' ||
        primaryComingWith === 'guest' ||
        primaryComingWith === 'introduction';
    /** Family/guest + attending: show members, transport, terms below primary (no separate step). */
    const showStepTwoExtras = primaryFamilyOrGuest && normalized(persons?.[0]?.eventChoice) === 'true' && !anyAlone;
    const isGuestFlow = primaryComingWith === 'guest' || primaryComingWith === 'introduction';
    const personsLimitReached = maxPersons != null && persons.length >= maxPersons;
    // Party size limit by ComingWith, main attendee included: Family max 5, Guest max 2. The backend enforces the same limits.
    const baseComingWithLimit = primaryComingWith === 'family' ? 5 : isGuestFlow ? 2 : null;
    const comingWithLimit = baseComingWithLimit != null && normalized(savedComingWith) === primaryComingWith
        ? Math.max(baseComingWithLimit, savedPersonCount)
        : baseComingWithLimit;
    const comingWithLimitReached = comingWithLimit != null && persons.length >= comingWithLimit;
    const addPersonCapped = personsLimitReached || comingWithLimitReached;
    const showAddPerson = !anyAlone && !anyEventNo && showStepTwoExtras && !addPersonCapped;
    const resetRemoveMemberDialog = () => {
        setRemoveMemberConfirmOpen(false);
        setPendingRemoveMemberIndex(null);
    };
    const requestRemoveMember = (index) => {
        if (index <= 0)
            return;
        setPendingRemoveMemberIndex(index);
        setRemoveMemberConfirmOpen(true);
    };
    const confirmRemoveMember = () => {
        const index = pendingRemoveMemberIndex;
        if (index == null || index <= 0) {
            resetRemoveMemberDialog();
            return;
        }
        const currentMembersCount = Math.max(0, fields.length - 1);
        remove(index);
        if (currentMembersCount === 1) {
            setValue?.('persons.0.ComingWith', 'alone', { shouldDirty: true, shouldValidate: true });
        }
        resetRemoveMemberDialog();
    };
    // Transport summary calculations (include primary + all members/guests)
    const autoPeopleCount = persons.filter((p, idx) => normalized(p?.TravelMode) === 'auto' && isPersonAttendingForTransport(p, idx)).length;
    const busPeopleCount = persons.filter((p, idx) => normalized(p?.TravelMode) === 'bus' && isPersonAttendingForTransport(p, idx)).length;
    const planePeople = persons.filter((p, idx) => normalized(p?.TravelMode) === 'plane' && isPersonAttendingForTransport(p, idx));
    const planePeopleCount = planePeople.length;
    const getRelationOptions = isGuestFlow ? getGuestRelationOptions : getFamilyRelationOptions;
    const relationLabelMap = useMemo(() => getRelationOptions(t).reduce((acc, o) => {
        acc[o.value] = o.label;
        return acc;
    }, {}), [t]);
    const transportLocationLabelMap = useMemo(() => {
        const acc = {};
        Array.from(allPickupOptionsById.values()).forEach((option) => {
            acc[option.value] = getPickupLocationInputLabel(option);
        });
        persons.forEach((person) => {
            const saved = buildSavedPickupLocationOption(person);
            if (saved && !acc[saved.value])
                acc[saved.value] = getPickupLocationInputLabel(saved);
        });
        return acc;
    }, [allPickupOptionsById, persons]);
    const busPeople = persons.filter((p, idx) => normalized(p?.TravelMode) === 'bus' && isPersonAttendingForTransport(p, idx));
    const busSelectedLocations = Array.from(new Set(busPeople
        .map((p) => String(p?.transportLocation ?? '').trim())
        .filter(Boolean)
        .map((loc) => transportLocationLabelMap[loc])
        .filter(Boolean)));
    const busLocationCounts = busPeople.reduce((acc, p) => {
        const locValue = String(p?.transportLocation ?? '').trim();
        if (!locValue)
            return acc;
        const locLabel = transportLocationLabelMap[locValue];
        if (!locLabel)
            return acc;
        acc[locLabel] = (acc[locLabel] ?? 0) + 1;
        return acc;
    }, {});
    const watchedVehicleCount = useWatch({ control, name: 'vehicleCount' });
    const vehicleCount = Math.max(1, Number(watchedVehicleCount) || 1);
    useEffect(() => {
        if ((primaryComingWith === 'family' ||
            primaryComingWith === 'guest' ||
            primaryComingWith === 'introduction') &&
            autoPeopleCount > 0 &&
            (watchedVehicleCount === undefined ||
                watchedVehicleCount === null ||
                watchedVehicleCount === '' ||
                Number(watchedVehicleCount) === 0)) {
            setValue?.('vehicleCount', 1);
        }
    }, [primaryComingWith, autoPeopleCount, setValue, watchedVehicleCount]);
    useEffect(() => {
        if (autoPeopleCount === 0) {
            if (watchedVehicleCount !== null &&
                watchedVehicleCount !== undefined &&
                watchedVehicleCount !== '') {
                setValue?.('vehicleCount', null);
            }
        }
        else if (vehicleCount > autoPeopleCount) {
            setValue?.('vehicleCount', autoPeopleCount);
        }
    }, [autoPeopleCount]);
    const bgColor = theme.palette.common.grey8;
    const timelineOptions = [];
    // Single linear column: primary, then (family/guest + attending) members + transport + terms below
    timelineOptions.push({
        step: t('familyForm.personalDetails') || 'Personal Details',
        content: (_jsxs(Box, { sx: { mt: 2, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(FamilyPersonCard, { index: 0, displayNumber: 1, control: control, errors: errors, persons: persons, isRegistered: false, isEditMode: isEditMode, primaryComingWith: primaryComingWith, showStepTwoFields: false, isAdditionalPerson: false, totalCount: fields.length, onRemove: requestRemoveMember, setValue: setValue, companyOptions: companyOptions, pickupLocationOptions: pickupLocationOptions }), isSaved && anyAlone && (_jsx(Box, { sx: {
                        borderRadius: '12px',
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                        backgroundColor: theme.palette.background.paper,
                        px: 2.5,
                        py: 2
                    }, children: _jsx(ECheckboxGroup, { control: control, name: 'persons.0.TermsAccepted', label: '', bgColor: 'transparent', options: [
                            {
                                label: termsAndPrivacyLabel,
                                value: 'true'
                            }
                        ], rules: termsAcceptedRules(t), error: errors?.persons?.[0]
                            ?.TermsAccepted?.message }) })), showStepTwoExtras && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'subtitle2', fontWeight: 600, color: 'text.primary', sx: { mt: 1 }, children: t('familyForm.companyGroup') }), fields
                            .map((person, idx) => ({ person, idx }))
                            .filter(({ idx }) => idx > 0)
                            .map(({ person, idx: index }, visibleIndex) => (_jsx(FamilyPersonCard, { index: index, displayNumber: visibleIndex + 1, control: control, errors: errors, persons: persons, isRegistered: false, primaryComingWith: primaryComingWith, showStepTwoFields: false, isAdditionalPerson: true, totalCount: fields.length, onRemove: requestRemoveMember, setValue: setValue, companyOptions: companyOptions, pickupLocationOptions: getPickupOptionsForPerson(persons?.[index]) }, person.id))), showStepTwoExtras && !anyAlone && !anyEventNo && personsLimitReached && (_jsx(Alert, { severity: 'info', sx: { borderRadius: 1.5 }, children: t('familyForm.maxPersonsReached', { count: maxPersons }) })), showStepTwoExtras &&
                            !anyAlone &&
                            !anyEventNo &&
                            !personsLimitReached &&
                            comingWithLimitReached && (_jsx(Alert, { severity: 'info', sx: { borderRadius: 1.5 }, children: t('familyForm.maxPersonsReached', { count: comingWithLimit }) })), showAddPerson && (_jsx(Box, { children: _jsx(Button, { variant: 'outlined', color: 'inherit', sx: {
                                    borderRadius: '8px',
                                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                    textTransform: 'none'
                                }, startIcon: _jsx(Icon, { icon: 'eva:plus-outline', width: 16, height: 16 }), onClick: () => {
                                    const primary = persons?.[0];
                                    const inheritedTravelMode = normalized(primary?.TravelMode) === 'plane' ? '' : (primary?.TravelMode ?? '');
                                    append({
                                        ...EMPTY_PERSON,
                                        TravelMode: inheritedTravelMode,
                                        transportLocation: '',
                                        relationshipToYou: isGuestFlow ? 'guest' : undefined
                                    });
                                }, children: t('familyForm.addPerson') || 'Add Person' }) })), (primaryComingWith === 'family' ||
                            primaryComingWith === 'guest' ||
                            primaryComingWith === 'introduction') && (_jsxs(Box, { sx: {
                                borderRadius: '12px',
                                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                                backgroundColor: theme.palette.background.paper,
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5
                            }, children: [_jsx(Typography, { variant: 'subtitle1', fontWeight: 500, color: 'text.primary', children: t('familyForm.transportSummary') || 'Transport Summary' }), _jsx(Typography, { color: 'text.primary', fontSize: 14, children: persons.length === 1
                                        ? t('familyForm.totalPeopleOne', { defaultValue: '1 person in total' })
                                        : t('familyForm.totalPeopleMany', {
                                            count: persons.length,
                                            defaultValue: '{{count}} people in total'
                                        }) }), autoPeopleCount > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { color: 'text.primary', fontSize: 14, children: autoPeopleCount === 1
                                                ? t('familyForm.autoPeopleSingle', { count: autoPeopleCount }) ||
                                                    `${autoPeopleCount} person traveling by car`
                                                : t('familyForm.autoPeopleMultiple', { count: autoPeopleCount }) ||
                                                    `${autoPeopleCount} persons traveling by car` }), _jsx(Grid, { container: true, spacing: 1, children: _jsx(Grid, { item: true, xs: 12, children: _jsx(ETextField, { control: control, label: t('familyForm.numberOfVehicles') || 'Number of vehicles', name: 'vehicleCount', bgColor: bgColor, disabled: autoPeopleCount === 0, InputProps: {
                                                        readOnly: true,
                                                        endAdornment: (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: [_jsx(IconButton, { size: 'small', onClick: () => setValue?.('vehicleCount', Math.max(1, vehicleCount - 1)), disabled: autoPeopleCount === 0 || vehicleCount <= 1, sx: {
                                                                        width: 28,
                                                                        height: 28,
                                                                        border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                                                                        borderRadius: 1,
                                                                        fontSize: 16
                                                                    }, children: "-" }), _jsx(Typography, { fontSize: 14, sx: { minWidth: 16, textAlign: 'center' }, children: vehicleCount }), _jsx(IconButton, { size: 'small', onClick: () => setValue?.('vehicleCount', vehicleCount + 1), disabled: autoPeopleCount === 0 || vehicleCount >= autoPeopleCount, sx: {
                                                                        width: 28,
                                                                        height: 28,
                                                                        border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                                                                        borderRadius: 1,
                                                                        fontSize: 16
                                                                    }, children: "+" })] }))
                                                    } }) }) })] })), busPeopleCount > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { color: 'text.primary', fontSize: 14, children: busPeopleCount === 1
                                                ? t('familyForm.busPeopleSingle') || '1 person traveling by bus'
                                                : t('familyForm.busPeopleMultiple', { count: busPeopleCount }) ||
                                                    `${busPeopleCount} persons traveling by bus` }), _jsx(Box, { sx: {
                                                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                                                backgroundColor: 'white',
                                                minHeight: '50px',
                                                padding: '16px',
                                                borderRadius: 1
                                            }, children: busSelectedLocations.length > 0 ? (busSelectedLocations.map((location) => (_jsxs(Typography, { color: 'text.primary', fontSize: 14, children: [location, " : ", busLocationCounts[location] ?? 0] }, location)))) : (_jsx(Typography, { color: 'text.primary', fontSize: 14, children: "-" })) })] })), planePeopleCount > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { color: 'text.primary', fontSize: 14, children: planePeopleCount === 1
                                                ? t('familyForm.planePeopleSingle', { defaultValue: '1 person traveling by plane' })
                                                : t('familyForm.planePeopleMultiple', { count: planePeopleCount, defaultValue: `${planePeopleCount} persons traveling by plane` }) }), _jsx(Box, { sx: {
                                                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                                                backgroundColor: 'white',
                                                minHeight: '50px',
                                                padding: '16px',
                                                borderRadius: 1
                                            }, children: planePeople.map((p, i) => {
                                                const name = [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim() || t('familyForm.personFallback', { number: i + 1, defaultValue: `Person ${i + 1}` });
                                                const relationKey = String(p?.relationshipToYou ?? '').trim();
                                                const relationLabel = relationKey ? (relationLabelMap[relationKey] ?? relationKey) : '';
                                                const label = !relationLabel ? name : `${name} (${relationLabel})`;
                                                return (_jsx(Typography, { color: 'text.primary', fontSize: 14, children: label }, i));
                                            }) })] }))] })), _jsx(Box, { sx: {
                                borderRadius: '12px',
                                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                                backgroundColor: theme.palette.background.paper,
                                px: 2.5,
                                py: 2
                            }, children: _jsx(ECheckboxGroup, { control: control, name: 'persons.0.TermsAccepted', label: '', bgColor: 'transparent', options: [
                                    {
                                        label: termsAndPrivacyLabel,
                                        value: 'true'
                                    }
                                ], rules: termsAcceptedRules(t), error: errors?.persons?.[0]
                                    ?.TermsAccepted?.message }) })] }))] }))
    });
    return (_jsxs(_Fragment, { children: [_jsx(HTimeline, { options: timelineOptions }), _jsx(ConfirmationDialog, { open: removeMemberConfirmOpen, onClose: resetRemoveMemberDialog, title: t('familyForm.removeMemberTitle') || 'Remove members', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, size: 'large', variant: 'outlined', color: 'inherit', onClick: resetRemoveMemberDialog, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, size: 'large', variant: 'contained', color: 'inherit', onClick: confirmRemoveMember, children: t('actions.confirm') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('familyForm.confirmRemoveMember') || 'Are you sure you want to remove this member?' }) }) }), _jsx(FamilyLegalTermsDialog, { open: termsDialogOpen, onClose: () => setTermsDialogOpen(false) }), _jsx(FamilyLegalPrivacyDialog, { open: privacyDialogOpen, onClose: () => setPrivacyDialogOpen(false) })] }));
};
export default FamilyFormContent;
